<template>
  <div>
    <div class="page-heading" role="heading">
      <h1 class="page-heading__header">Booking {{ booking.number }}</h1>
    </div>

    <section>
      <!-- Booking Card -->
      <div
        class="t-mt-10 t-shadow t-px-5 t-pt-3 t-pb-4 t-bg-white md:t-rounded-lg"
      >
        <div class="t-flex t-justify-between t-items-baseline t-py-2">
          <div class="t-flex t-space-x-2 t-items-center">
            <p class="t-text-2xl t-font-bold t-mr-5">
              {{ booking.number }}
            </p>

            <slot name="status"> </slot>

            <slot name="card-header-side"> </slot>
          </div>
          <div class="w-1/2">
            <multiselect
              v-model="truckAllocation"
              :options="trucks"
              placeholder="Allocate a truck"
              :custom-label="nameWithLang"
              track-by="id"
              @input="handleTruckAllocation"
            >
            </multiselect>
          </div>
          <p class="t-text-xl">${{ booking.price }}</p>
        </div>

        <div
          class="
            t-flex t-flex-col
            md:t-flex-row md:t-justify-between
            t-border-t-2 t-border-gray-300 t-pb-2
            md:t-pb-0
          "
        >
          <div
            class="
              t-grid t-grid-rows-3 t-grid-cols-1 t-text-sm t-py-1
              md:t-grid-rows-1
              md:t-grid-cols-3
              md:t-divide-x-2
              md:t-divide-gray-300
            "
          >
            <div class="t-px-2 t-py-1">
              <p class="t-text-gray-700 t-uppercase t-text-xs t-font-semibold">
                Booking Reference
              </p>
              <p>{{ booking.sender_ref }}</p>
            </div>

            <div class="t-px-2 t-py-1">
              <p class="t-text-gray-700 t-uppercase t-text-xs t-font-semibold">
                Placed
              </p>
              <p>{{ bookingPlacedAt(booking.placed_at) }}</p>
            </div>

            <div class="t-px-2 t-py-1">
              <p class="t-text-gray-700 t-uppercase t-text-xs t-font-semibold">
                Pickup Carrier
              </p>

              <span v-if="booking.status >= 3">
                {{ booking.carrier.name }}
              </span>
              <span v-else>
                Pending&nbsp;
                <span
                  v-tooltip:bottom="[
                    '<h6>Why does it say pending?</h6> Because we\'re filling empty space on trucks around New Zealand it can\
                    take a little while for us to confirm exactly who the carrier will be.\
                    Your booking is 100% guaranteed at the price you booked at, as we have\
                    a vast network of carriers around the country',
                    true
                  ]"
                >
                  <i class="far fa-info-circle"></i>
                </span>
              </span>
            </div>
          </div>

          <slot name="card-side-right"> </slot>
        </div>
      </div>

      <section
        class="
          t-flex t-flex-col
          md:t-flex-row
          t-justify-between t-mt-8 t-space-y-4
          md:t-space-y-0
          t-space-x-0
          md:t-space-x-8
        "
      >
        <!-- Pickup Location -->
        <div
          v-if="pickupLocation"
          class="t-flex-grow t-bg-white t-rounded-md t-shadow t-p-5"
        >
          <div class="t-border-b-2 t-border-gray-500 t-mb-4 t-pb-4">
            <p class="t-font-bold">Pickup Location</p>
          </div>
          <div>
            <p class="t-text-xs t-uppercase t-text-blue-700 t-font-semibold">
              From
            </p>

            <p class="t-font-bold">{{ pickupLocation.name }}</p>
            <p>
              {{ pickupLocation.address.street_number }}
              {{ pickupLocation.address.street_name }}
            </p>
            <p>{{ pickupLocation.address.suburb }}</p>
            <p>{{ pickupLocation.address.city }}</p>

            <p class="t-mt-2 t-mb-5">
              After {{ booking.pickup_at || booking.pickup_after }}
            </p>

            <div
              class="
                t-flex t-flex-col
                md:t-flex-row
                t-space-x-0
                md:t-space-x-20
                t-space-y-2
                md:t-space-y-0
              "
            >
              <div>
                <p
                  class="t-text-xs t-uppercase t-text-gray-700 t-font-semibold"
                >
                  Sender Reference
                </p>
                <p>
                  {{ pickupLocation.ref || booking.pickup_ref || "&nbsp;" }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Delivery Location -->
        <div
          v-if="deliveryLocation"
          class="t-flex-grow t-bg-white t-rounded-md t-shadow t-p-5"
        >
          <div class="t-border-b-2 t-border-gray-500 t-mb-4 t-pb-4">
            <p class="t-font-bold">Delivery Location</p>
          </div>
          <div>
            <p class="t-text-xs t-uppercase t-text-blue-700 t-font-semibold">
              To
            </p>

            <p class="t-font-bold">{{ deliveryLocation.name }}</p>
            <p>
              {{ deliveryLocation.address.street_number }}
              {{ deliveryLocation.address.street_name }}
            </p>
            <p>{{ deliveryLocation.address.suburb }}</p>
            <p>{{ deliveryLocation.address.city }}</p>

            <p class="t-mt-2 t-mb-5">
              Before {{ booking.deliver_at || booking.deliver_before }}
            </p>

            <div
              class="
                t-flex t-flex-col
                md:t-flex-row
                t-space-x-0
                md:t-space-x-20
                t-space-y-2
                md:t-space-y-0
              "
            >
              <div>
                <p
                  class="t-text-xs t-uppercase t-text-gray-700 t-font-semibold"
                >
                  Receiver Reference
                </p>
                <p>{{ deliveryLocation.ref || booking.delivery_ref || " " }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </section>

    <!-- Tracking Section -->
    <section class="t-mt-8 t-rounded-md t-p-5 t-shadow t-bg-white">
      <div class="t-flex t-justify-between">
        <h3 class="t-font-bold">
          Tracking History
        </h3>
        <button
          @click="refreshRoute"
          class="t-px-3 t-py-1 t-bg-blue-500 t-text-white t-rounded-md t-text-sm hover:t-bg-blue-700"
        >
          Refresh Map
        </button>
      </div>

      <div class="t-flex t-flex-justify-between t-mt-4">
        <freight-movement-timeline
          :freight-movements="mapMovementTimeline"
          :booking="booking"
        />

        <div class="t-w-10/20">
          <span v-if="loadingRoute">Loading...</span>
          <span v-else>
            <directions
              :origin="mapData.pickupLocation"
              :destination="mapData.deliveryLocation"
              :latestPoint="latestPoint"
              :movements="mapData.rawMapMovements"
              :height="500"
            />
          </span>
        </div>
      </div>
    </section>

    <!-- Freight Info Section -->
    <section class="t-rounded-md t-mt-8 t-p-5 t-shadow t-bg-white">
      <h3 class="t-font-bold t-pb-4">Freight</h3>
      <freight-table :freight-lines="booking.lines" />
    </section>
  </div>
</template>

<script>
import moment from "moment";
import FreightMovementTimeline from "./FreightMovementTimeline";
import Directions from "../../maps/Directions";
import FreightTable from "./FreightTable";
import Multiselect from "vue-multiselect";

export default {
  name: "BaseBookingDetailsPage",
  components: {
    FreightMovementTimeline,
    Directions,
    FreightTable,
    Multiselect
  },
  props: {
    isLoading: {},
    booking: {},
    mapData: {},
    mapMovementTimeline: {}
  },
  data() {
    return {
      latestPoint: { lat: null, lng: null },
      pickupLocation: null,
      deliveryLocation: null,
      trucks: [],
      routes: [],
      loadingRoute: false,
      loading: false,
      loadingTruck: false,
      truckAllocation:
        this.booking.truck_allocation_id == null
          ? {
              id: null,
              plate: null,
              display_name: "Unallocated"
            }
          : JSON.parse(this.booking.truck_allocation_id)
    };
  },
  mounted() {
    this.loadTrucks();
  },
  created() {
    this.pickupLocation = this.booking.pickup_location;
    this.deliveryLocation = this.booking.delivery_location;
    this.loadRoute(
      moment()
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss"),
      moment()
        .endOf("day")
        .format("YYYY-MM-DD HH:mm:ss")
    );
  },
  methods: {
    loadRoute(startTime, endTime) {
      if (this.truckAllocation.id === null) {
        return;
      }

      this.loadingRoute = true;
      api
        .get(
          "/carrier/gps/stops?truck_id=" +
            this.truckAllocation.id +
            "&start_time=" +
            startTime +
            "&end_time=" +
            endTime +
            "&include=startPoint&sort=asc&previous=included"
        )
        .then(response => {
          this.routes = response.data.data;

          if (this.routes.length) {
            let lastRoute = this.routes[this.routes.length - 1];
            this.latestPoint.lat = lastRoute.lat;
            this.latestPoint.lng = lastRoute.lng;
          } else {
            this.latestPoint.lat = null;
            this.latestPoint.lng = null;
          }

          this.loadingRoute = false;
        })
        .catch(error => {
          console.log(error);
          this.$Message.error({
            content: "Failed to load GPS data",
            duration: 10,
            closable: true
          });
        })
        .then(() => {
          this.loadingRoute = false;
        });
    },
    bookingPlacedAt(consignmentPlacedAt) {
      return moment
        .utc(consignmentPlacedAt.toString(), "hh:mm a ddd Do MMM")
        .local()
        .format("ddd Do MMM hh:mm a");
    },
    loadTrucks() {
      this.loadingTruck = true;

      let defaultOption = {
        id: null,
        plate: null,
        display_name: "Unallocated"
      };

      api.get("/carrier/gps/trucks").then(response => {
        this.trucks = response.data.data.map(truck => {
          return {
            id: truck.id,
            plate: truck.plate,
            display_name: truck.display_name
          };
        });
        this.trucks.unshift(defaultOption);
        this.loadingTruck = false;
      });
    },
    nameWithLang({ plate, display_name }) {
      return plate ? `${display_name} - (${plate})` : `${display_name}`;
    },
    handleTruckAllocation(value, id) {
      this.truckAllocation = value;

      axios
        .put("/carry-freight/bookings/" + this.booking.id + "/allocate-truck", {
          truckAllocation: this.truckAllocation
        })
        .then(response => {
          let data = response.data;

          if (data.is_success) {
            this.$swal({
              title: data.message,
              type: "success"
            }).then(() => location.reload());
            this.isDisabled = false;
          } else {
            this.$swal({
              title: data.message,
              type: "error"
            }).then(() => location.reload());
          }
        })
        .catch(e => {
          Event.$emit("serverError");
          console.log(e);
        });
    },
    refreshRoute() {
      this.loadRoute(
        moment()
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss"),
        moment()
          .endOf("day")
          .format("YYYY-MM-DD HH:mm:ss")
      );
    }
  }
};
</script>

<style scoped></style>
